import { Link, useLocation, useNavigate } from 'react-router-dom';
import { X, SignOut, Question, UserCircle } from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onContactSupport?: () => void;
}

export default function MobileMenu({ isOpen, onClose, onContactSupport }: MobileMenuProps) {
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      onClose();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleNavigation = (to: string) => {
    onClose();
    navigate(to);
  };

  const handleContactSupport = () => {
    onClose();
    if (onContactSupport) {
      onContactSupport();
    }
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 left-0 h-full w-[80%] max-w-[320px] bg-blackboard border-r border-border z-50 transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-3">
              <img 
                src="/logo.png" 
                alt="EducAIte Logo" 
                className="h-8 w-auto object-contain"
              />
            </div>
            <button
              onClick={onClose}
              className="text-chalk-white hover:text-chalk-blue transition-colors p-2"
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          </div>

          {/* User Profile Section */}
          {isAuthenticated && user && (
            <div className="p-4 border-b border-border">
              <div className="flex items-center gap-3">
                {user.profileImage ? (
                  <div
                    className="bg-center bg-no-repeat aspect-square bg-cover rounded-full w-12 h-12 border-2 border-chalk-blue"
                    style={{ backgroundImage: `url(${user.profileImage})` }}
                  />
                ) : (
                  <div className="bg-chalk-blue rounded-full w-12 h-12 flex items-center justify-center text-blackboard text-lg font-bold">
                    {user.fullName.charAt(0)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-chalk-white font-medium truncate">{user.fullName}</div>
                  <div className="text-text-secondary text-sm truncate">{user.email}</div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Links */}
          <nav className="flex-1 overflow-y-auto py-4">
            <div className="space-y-1 px-2">
              <button
                onClick={() => handleNavigation('/dashboard')}
                className={`flex items-center w-full px-4 py-3 rounded-lg transition-colors ${
                  isActive('/dashboard')
                    ? 'bg-chalk-blue/10 text-chalk-blue'
                    : 'text-chalk-white hover:bg-chalk-white/5'
                }`}
              >
                <span className="text-base font-medium">Dashboard</span>
              </button>

              <button
                data-tour="lesson-history-button"
                onClick={() => handleNavigation('/history')}
                className={`flex items-center w-full px-4 py-3 rounded-lg transition-colors ${
                  isActive('/history')
                    ? 'bg-chalk-blue/10 text-chalk-blue'
                    : 'text-chalk-white hover:bg-chalk-white/5'
                }`}
              >
                <span className="text-base font-medium">Lesson History</span>
              </button>

              <button
                onClick={() => handleNavigation('/progress-reports')}
                className={`flex items-center w-full px-4 py-3 rounded-lg transition-colors ${
                  isActive('/progress-reports')
                    ? 'bg-chalk-blue/10 text-chalk-blue'
                    : 'text-chalk-white hover:bg-chalk-white/5'
                }`}
              >
                <span className="text-base font-medium">Progress Reports</span>
              </button>

              <button
                onClick={() => handleNavigation('/settings/profile')}
                className={`flex items-center w-full px-4 py-3 rounded-lg transition-colors ${
                  isActive('/settings/profile') || isActive('/settings/subscription')
                    ? 'bg-chalk-blue/10 text-chalk-blue'
                    : 'text-chalk-white hover:bg-chalk-white/5'
                }`}
              >
                <UserCircle size={20} className="mr-3" />
                <span className="text-base font-medium">Profile & settings</span>
              </button>

              {/* Contact Support */}
              {onContactSupport && (
                <button
                  data-tour="support-button"
                  onClick={handleContactSupport}
                  className="flex items-center w-full px-4 py-3 rounded-lg text-chalk-white hover:bg-chalk-white/5 transition-colors"
                >
                  <Question size={20} className="mr-3" />
                  <span className="text-base font-medium">Contact Support</span>
                </button>
              )}
            </div>
          </nav>

          {/* Logout Button */}
          {isAuthenticated && (
            <div className="p-4 border-t border-border">
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <SignOut size={20} className="mr-3" />
                <span className="text-base font-medium">Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
